/**
 * The one button of Arrange, and what stands in for it while the work is done.
 *
 * Before it can be pressed it says what it is waiting for. While a scan is read, the words are
 * read or the arranging runs, it is the line saying which, with a way to stop. While photos of
 * the score are read the players can still be chosen, so the button stays above that line, shut,
 * saying so (`meanwhile`). The server
 * reports the stage a run is at and not how far through it is, so the bar under the line moves
 * without measuring: one that filled would be a figure nobody has.
 *
 * After an arrangement the button arranges again, for the reader who has changed a player or
 * the words, and where another version can be written there is a button for that under it
 * (`another`). Another version is another writing: the note model writes every part under the
 * tune again and no two takes are alike. Handing the players the score's own notes has one
 * outcome, so the button is not there to promise a second.
 */

export interface ArrangeActionProps {
  /** What is being done, in a sentence, or `null` where nothing is. */
  readonly busy: string | null;
  /** Why Arrange cannot be pressed, or `null` where it can. */
  readonly waiting: string | null;
  /** Said under the shut button while `busy`, where something can be done meanwhile. */
  readonly meanwhile: string | null;
  /** Whether an arrangement is on the page. */
  readonly arranged: boolean;
  /** Whether another version of it can be written. */
  readonly another: boolean;
  /** What went wrong with the last run, or `null`. */
  readonly trouble: string | null;
  onArrange(): void;
  onAnother(): void;
  onStop(): void;
}

export default function ArrangeAction({
  busy,
  waiting,
  meanwhile,
  arranged,
  another,
  trouble,
  onArrange,
  onAnother,
  onStop,
}: ArrangeActionProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2.5">
      {busy === null || meanwhile === null ? null : (
        <>
          <button
            type="button"
            disabled
            className="h-12 rounded-lg bg-shell-key text-[16px] font-semibold text-shell-text"
          >
            Arrange
          </button>
          <p className="text-[12px] text-shell-text">{meanwhile}</p>
        </>
      )}
      {busy !== null ? (
        <>
          <div
            role="status"
            className="flex min-h-12 items-center gap-3 rounded-lg bg-shell-key py-1.5 pl-4 pr-1.5 text-[14px] text-shell-lit"
          >
            <span className="flex-1">{busy}</span>
            <button
              type="button"
              onClick={onStop}
              className="h-9 shrink-0 rounded-md px-3 text-[13px] text-shell-text hover:bg-shell-hot hover:text-shell-lit"
            >
              Stop
            </button>
          </div>
          <div aria-hidden="true" className="h-[3px] overflow-hidden rounded-sm bg-shell-key">
            <div className="h-full w-1/3 animate-sweep rounded-sm bg-shell-live motion-reduce:w-full motion-reduce:animate-none" />
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onArrange}
            disabled={waiting !== null}
            className="h-12 rounded-lg bg-shell-live text-[16px] font-semibold text-white hover:brightness-110 disabled:bg-shell-key disabled:text-shell-text disabled:hover:brightness-100"
          >
            {arranged ? "Arrange again" : "Arrange"}
          </button>
          {waiting === null ? null : <p className="text-[12px] text-shell-text">{waiting}</p>}
          {!another ? null : (
            <button
              type="button"
              onClick={onAnother}
              className="h-11 rounded-lg border border-shell-seam text-[14px] text-shell-lit hover:bg-shell-hot"
            >
              Try another version
            </button>
          )}
        </>
      )}
      {trouble === null ? null : (
        <p role="alert" className="rounded-md bg-fault/20 px-3 py-2 text-[13px] text-shell-lit">
          {trouble}
        </p>
      )}
    </div>
  );
}
